/**
 * Entry rules for the resource capability grids (Configure → Method).
 *
 * Pure, so the cases that matter — a blank field, a decimal rank, 101% — are
 * unit tested rather than clicked. The server validates the same bounds; this
 * exists so the grid can refuse a value before spending a request on it, and
 * say why.
 */

/** The em-dash used wherever a grid cell has no value to show. */
export const EMPTY_CELL = '—'

export type ParsedNumber = { ok: true; value: number } | { ok: false; error: string }

/**
 * Preference is a RANK, lowest first: "1" is this person's preferred method.
 *
 * Whole numbers only, and at least 1 — a rank of 0 or 2.5 has no reading, and
 * accepting one would put a value in the matrix that the later assignment
 * ticket has to guess the meaning of.
 */
export function parsePreference(raw: string): ParsedNumber {
  const text = raw.trim()
  if (!text) return { ok: false, error: 'Preference is required.' }
  if (!/^\d+$/.test(text)) return { ok: false, error: 'Preference must be a whole number.' }
  const value = Number(text)
  if (value < 1) return { ok: false, error: 'Preference starts at 1 (lower is preferred).' }
  return { ok: true, value }
}

/**
 * Proficiency is a percentage, 0–100.
 *
 * Decimals are allowed: 87.5% is a meaningful figure and rounding it away would
 * be a data decision made by an input field.
 */
export function parseProficiency(raw: string): ParsedNumber {
  const text = raw.trim()
  if (!text) return { ok: false, error: 'Proficiency is required.' }
  if (!/^\d+(\.\d+)?$/.test(text)) return { ok: false, error: 'Proficiency must be a number.' }
  const value = Number(text)
  if (value < 0 || value > 100) return { ok: false, error: 'Proficiency is a percentage, 0–100.' }
  return { ok: true, value }
}

/** `75%`, or an em-dash when there is nothing to show. */
export function formatProficiency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_CELL
  // Trim a trailing `.0` so a whole percentage reads as "75%", not "75.0%".
  return `${Number(value.toFixed(1))}%`
}

/**
 * The Last Date Used cell.
 *
 * An em-dash is the expected value today, not a failure: the date is derived
 * from completed process-step executions matched by METHOD, and process steps do
 * not carry a method until a later ticket. Anything unparseable reads the same
 * way rather than printing "Invalid Date" at someone.
 */
export function formatLastUsed(iso: string | null | undefined): string {
  if (!iso) return EMPTY_CELL
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? EMPTY_CELL : date.toLocaleDateString()
}
